/* eslint-disable no-undef */
import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button as AntButton,
  App,
  Card,
  Dropdown,
  MenuProps,
  Modal,
  Spin,
  Table,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { useNavigate, useParams } from "react-router-dom";

import { ReactComponent as Plus } from "../../../../assets/add.svg";
import { ReactComponent as Ellipsis } from "../../../../assets/ellipsis.svg";
import { Button } from "../../../../custom";
import {
  deleteStudentActivity,
  getStudentActivityByStudentLifeId,
} from "../../../../requests";
import { sanitizeAndLimitString } from "../../../../utils/sanitizeAndLimitString";
import DeleteModalContent from "../../../deleteModal/deleteModal";

import StudentActivityForm from "./studentActivityForm";

const StudentActivity = () => {
  const { notification } = App.useApp();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [studentActivity, setStudentActivity] = useState<StudentActivities>(
    {} as StudentActivities
  );
  const [openDelete, setOpenDelete] = useState(false);

  const navigate = useNavigate();

  const deleteStudentActivityMutation = useMutation({
    mutationFn: deleteStudentActivity,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["get-support-guidance"],
    queryFn: () => getStudentActivityByStudentLifeId(id!),
    enabled: !!id,
  });

  const deleteStudentActivityHandler = async () => {
    try {
      await deleteStudentActivityMutation.mutateAsync(studentActivity?.id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          refetch();
          setOpenDelete((prevState) => !prevState);
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const columns: ColumnsType<StudentActivities> = [
    // {
    //   key: "id",
    //   title: "ID",
    //   dataIndex: "id",
    // },
    {
      key: "title",
      title: "Title",
      dataIndex: "title",
    },
    {
      key: "description",
      title: "Description",
      dataIndex: "description",
      render: (_: any, { description }: any) => {
        const limitedCleanHtml = sanitizeAndLimitString(description);
        return <div dangerouslySetInnerHTML={{ __html: limitedCleanHtml }} />;
      },
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "activeStatus",
      render: (_, { activeStatus }) => (activeStatus ? "Active" : "Inactive"),
    },
    {
      key: "action",
      title: "",
      render: (_, record) => {
        const items: MenuProps["items"] = [
          {
            key: "1",
            label: "Add Items",
            onClick: () => {
              navigate(`/student-life/${record.id}/student-activities-item`);
            },
          },
          {
            key: "2",
            label: "Edit",
            onClick: () => {
              setStudentActivity(record);
              setOpenEdit(true);
            },
          },
          {
            key: "3",
            label: "Delete",
            onClick: () => {
              setStudentActivity(record);
              setOpenDelete((prevState) => !prevState);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <AntButton type="text" icon={<Ellipsis />} />
          </Dropdown>
        );
      },
    },
  ];

  const studentActivityData = data?.data as StudentActivities[];

  const handleOpenModal = () => {
    setStudentActivity({} as StudentActivities);
    setOpen((prevState) => !prevState);
  };

  if (isLoading) {
    return <Spin />;
  }

  if (isError) {
    return <div>Error: {error?.message}</div>;
  }

  return (
    <div>
      <section className="space-between">
        <h3>Student Life: Student Activities Setup</h3>

        {studentActivityData?.length === 0 && (
          <Button
            onClick={handleOpenModal}
            iconBefore={<Plus />}
            text="Setup"
          />
        )}
      </section>

      <br />

      <Card bordered={false}>
        <Table
          dataSource={studentActivityData}
          columns={columns}
          pagination={{ position: ["bottomCenter"] }}
          rowKey={(record) => record.id}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        centered
        title="Create Student Activity"
        footer={null}
      >
        <StudentActivityForm
          item={studentActivity}
          handleClose={() => setOpen(false)}
        />
      </Modal>

      <Modal
        open={openEdit}
        onCancel={() => setOpenEdit(false)}
        centered
        title="Edit Student Activity"
        footer={null}
      >
        <StudentActivityForm
          item={studentActivity}
          handleClose={() => setOpenEdit(false)}
        />
      </Modal>
      <Modal
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        centered
        title="Delete Student Activity Setup"
        footer={null}
      >
        <DeleteModalContent
          isLoading={deleteStudentActivityMutation?.isPending}
          handleCloseModal={() => setOpenDelete(false)}
          handleSubmit={deleteStudentActivityHandler}
          title={studentActivity?.title}
        />
      </Modal>
    </div>
  );
};

export default StudentActivity;
